import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { PhoneCall, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CALL_OUTCOMES = [
  ['connected', 'Connected'], ['voicemail', 'Left voicemail'], ['no_answer', 'No answer'],
  ['wrong_number', 'Wrong number'], ['call_back', 'Call back later'],
];

// "How did the call go?" - logs a Call activity (+ optional follow-up task) on the record.
export default function CallLogDialog({ open, onOpenChange, record, parentType, phone, onLogged }) {
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setOutcome(''); setNote(''); setFollowUp(''); };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/activities/call-log`, {
        parent_type: parentType, parent_id: record.id, outcome, phone, note: note.trim() || null,
        follow_up_at: followUp ? new Date(`${followUp}T09:00:00`).toISOString() : null,
      });
      toast.success(data.task ? 'Call logged + follow-up task added' : 'Call logged');
      onLogged && onLogged(data.last_call);
      reset(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not log call'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-white max-w-md" data-testid="call-log-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900"><PhoneCall className="w-4 h-4 text-[#00A79D]" /> How did the call go?</DialogTitle>
          <DialogDescription className="text-gray-500">{record?.full_name || record?.name || record?.email}{phone ? ` · ${phone}` : ''}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2" data-testid="call-outcomes">
          {CALL_OUTCOMES.map(([val, lab]) => (
            <button key={val} type="button" onClick={() => setOutcome(val)} data-testid={`call-outcome-${val}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${outcome === val ? 'bg-[#00A79D] border-[#00A79D] text-[#ffffff]' : 'border-gray-200 text-gray-700 hover:border-[#00A79D]'}`}>
              {lab}
            </button>
          ))}
        </div>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Notes (optional)"
          className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-900" data-testid="call-log-note" />
        <label className="text-xs font-semibold text-gray-600 flex items-center justify-between gap-3">
          Follow-up date (creates a task)
          <input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)}
            className="border border-gray-200 rounded-md px-2 py-1 text-sm text-gray-900" data-testid="call-log-followup" />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => onOpenChange(false)} className="text-sm px-3 py-2 text-gray-600" data-testid="call-log-cancel">Skip</button>
          <button type="button" disabled={!outcome || saving} onClick={save} data-testid="call-log-save"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-md bg-[#00A79D] hover:bg-[#00857D] text-[#ffffff] disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save call
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
